// What a volunteer may do about checking in to one of their own shifts, right now.
// Mirrors the App Backend's selfCheckInWindow (volunteers.controller.ts); the server is the
// authority and refuses outside its window, so this only decides which button to show.

/** Self check-in opens this long before the shift starts. Keep in step with the backend. */
export const CHECK_IN_OPENS_MINUTES = 30;

export type CheckInState =
  | { kind: 'none' }
  | { kind: 'not-yet'; opensAt: string }
  | { kind: 'check-in' }
  | { kind: 'check-out' };

interface RegistrationLike {
  status: string;
  checkedIn: boolean;
  checkOutTime?: string | null;
  shift?: { date: string; startTime: string; endTime: string };
}

// ponytail: reads the shift's times in the device's time zone. Volunteers are in the UK, so
// that is UK time; a phone set to another zone shows the button at the wrong time, and the
// server still refuses outside its own (UK) window.
function at(date: string, hhmm: string): Date {
  return new Date(`${date.slice(0, 10)}T${hhmm}:00`);
}

const sameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString();

export function checkInState(reg: RegistrationLike, now: Date = new Date()): CheckInState {
  if (reg.status !== 'confirmed' || !reg.shift || reg.checkOutTime) return { kind: 'none' };
  const start = at(reg.shift.date, reg.shift.startTime);
  let end = at(reg.shift.date, reg.shift.endTime);
  if (end <= start) end = new Date(end.getTime() + 86_400_000);

  if (reg.checkedIn) {
    const dayEnd = new Date(end);
    dayEnd.setHours(23, 59, 59, 999);
    return now <= dayEnd ? { kind: 'check-out' } : { kind: 'none' };
  }

  const opens = new Date(start.getTime() - CHECK_IN_OPENS_MINUTES * 60_000);
  if (now < opens) {
    return sameDay(now, start)
      ? { kind: 'not-yet', opensAt: opens.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) }
      : { kind: 'none' };
  }
  return now <= end ? { kind: 'check-in' } : { kind: 'none' };
}
